import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getToken, getChats, getChatUsuarios } from "@/lib/api";
import { nombreVisible } from "@/lib/clienteNombre";

const ChatContext = createContext(null);

const RECONNECT_MS = 1500;
const PING_MS = 25000;

function buildWsUrl() {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/ws/chat?token=${encodeURIComponent(getToken() || "")}`;
}

/**
 * Estado compartido del chat: socket en tiempo real (/api/ws/chat), lista de chats con no leídos
 * (badge del menú y sidebar del chat) y usuarios del equipo para avatares, menciones y asignaciones.
 */
export function ChatProvider({ children }) {
  const { user } = useAuth();
  const enabled = !!user && user.permissions?.chat !== false;
  const [chats, setChats] = useState([]);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [conectado, setConectado] = useState(false);
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const refreshTimerRef = useRef(null);
  const chatAbiertoRef = useRef(null);
  const chatsRef = useRef([]);
  chatsRef.current = chats;

  const refreshChats = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await getChats();
      setChats(data.chats || []);
    } catch (_) {
      /* silencioso: el badge simplemente no se actualiza */
    } finally {
      setChatsLoaded(true);
    }
  }, [enabled]);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(refreshChats, 400);
  }, [refreshChats]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
  }, []);

  /** La conversación abierta se registra para no avisar con toast de menciones que ya se están viendo. */
  const setChatAbierto = useCallback((clienteId) => {
    chatAbiertoRef.current = clienteId;
  }, []);

  /** Pone a cero el contador de un chat sin esperar a la API (al abrirlo / leerlo). */
  const marcarLeidoLocal = useCallback((clienteId) => {
    setChats((prev) => prev.map((c) => (c.cliente.id === clienteId ? { ...c, no_leidos: 0, menciones: 0 } : c)));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setChats([]);
      setUsuarios([]);
      return;
    }
    refreshChats();
    getChatUsuarios().then((d) => setUsuarios(d.usuarios || [])).catch(() => {});
  }, [enabled, refreshChats]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cerrado = false;
    let reconnectTimer = null;
    let pingTimer = null;

    const connect = () => {
      if (cerrado || !getToken()) return;
      const ws = new WebSocket(buildWsUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        setConectado(true);
        // Al reconectar puede haberse perdido algo: refrescar lista
        refreshChats();
        listenersRef.current.forEach((fn) => fn({ type: "reconectado" }));
        pingTimer = setInterval(() => send({ type: "ping" }), PING_MS);
      };
      ws.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (!msg?.type || msg.type === "pong") return;
        listenersRef.current.forEach((fn) => fn(msg));
        if (msg.type === "escribiendo") return;
        scheduleRefresh();
        if (msg.type === "mensaje_nuevo" && msg.mensaje?.menciones?.includes(user?.email) && chatAbiertoRef.current !== msg.cliente_id) {
          const chat = chatsRef.current.find((c) => c.cliente.id === msg.cliente_id);
          toast.message(`Te han mencionado en ${chat ? nombreVisible(chat.cliente) : "un chat"}`, {
            description: (msg.mensaje.texto || "").slice(0, 120),
          });
        }
      };
      ws.onclose = () => {
        setConectado(false);
        clearInterval(pingTimer);
        if (wsRef.current === ws) wsRef.current = null;
        if (!cerrado) reconnectTimer = setTimeout(connect, RECONNECT_MS);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (_) {}
      };
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!wsRef.current || wsRef.current.readyState > WebSocket.OPEN) {
        clearTimeout(reconnectTimer);
        connect();
      } else {
        refreshChats();
      }
    };

    connect();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cerrado = true;
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      document.removeEventListener("visibilitychange", onVisible);
      try {
        wsRef.current?.close();
      } catch (_) {}
      wsRef.current = null;
    };
  }, [enabled, user?.email, refreshChats, scheduleRefresh, send]);

  const usuariosByEmail = useMemo(() => Object.fromEntries(usuarios.map((u) => [u.email, u])), [usuarios]);
  const totalNoLeidos = useMemo(() => chats.reduce((acc, c) => acc + (c.no_leidos || 0), 0), [chats]);

  const value = useMemo(
    () => ({
      enabled,
      conectado,
      chats,
      chatsLoaded,
      refreshChats,
      marcarLeidoLocal,
      totalNoLeidos,
      usuarios,
      usuariosByEmail,
      subscribe,
      send,
      setChatAbierto,
    }),
    [enabled, conectado, chats, chatsLoaded, refreshChats, marcarLeidoLocal, totalNoLeidos, usuarios, usuariosByEmail, subscribe, send, setChatAbierto]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat debe usarse dentro de ChatProvider");
  return ctx;
}

/** Nombre corto de un usuario a partir de su email. */
export function useNombreUsuario() {
  const { usuariosByEmail } = useChat();
  return useCallback((email) => usuariosByEmail[email]?.nombre || (email || "").split("@")[0], [usuariosByEmail]);
}
